/**
 * Clipboard Utility with cross-device fallbacks
 * 
 * Handles quirks across browsers and devices:
 * - Safari: Only works in response to user gestures, HTTPS required
 * - Samsung/Android with third-party keyboards: navigator.clipboard.writeText()
 *   can silently succeed without actually writing to system clipboard.
 *   Third-party keyboards (Gboard, SwiftKey, etc.) can intercept clipboard access.
 * - Older browsers: No Clipboard API support
 * 
 * Strategy: Try multiple methods in order of reliability, using the legacy
 * execCommand approach as primary on Android (more reliable with third-party
 * keyboards) and Clipboard API as primary elsewhere.
 */

const isAndroid = (): boolean =>
  /android/i.test(navigator.userAgent);

/**
 * Legacy copy using a temporary textarea + execCommand.
 * More reliable on Android/Samsung with third-party keyboards because it
 * goes through the OS-level text selection → copy flow rather than the
 * Clipboard API permission model that keyboards can interfere with.
 */
function legacyCopy(text: string): boolean {
  const textarea = document.createElement('textarea');
  textarea.value = text;

  // Make invisible but keep it in the viewport so focus/select works
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '0';
  textarea.style.top = '0';
  textarea.style.width = '2em';
  textarea.style.height = '2em';
  textarea.style.padding = '0';
  textarea.style.border = 'none';
  textarea.style.outline = 'none';
  textarea.style.boxShadow = 'none';
  textarea.style.background = 'transparent';
  textarea.style.opacity = '0';
  // Prevent zoom on iOS
  textarea.style.fontSize = '16px';

  document.body.appendChild(textarea);

  // Focus is critical on Samsung — without it, execCommand('copy') silently fails
  textarea.focus();
  textarea.select();

  // iOS Safari needs both selection range AND setSelectionRange
  textarea.setSelectionRange(0, text.length);

  const range = document.createRange();
  range.selectNodeContents(textarea);
  const selection = window.getSelection();
  if (selection) {
    selection.removeAllRanges();
    selection.addRange(range);
  }

  let success = false;
  try {
    success = document.execCommand('copy');
  } catch {
    success = false;
  }

  document.body.removeChild(textarea);
  return success;
}

/**
 * Copy text to clipboard with cross-device fallbacks.
 *
 * On Android (Samsung, etc.): tries legacy execCommand first since it's more
 * reliable when third-party keyboards are active, then falls back to Clipboard API.
 *
 * On other platforms: tries Clipboard API first, then legacy fallback.
 *
 * @param text - The text to copy
 * @returns Promise that resolves to true if copy succeeded, false otherwise
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // On Android, prefer legacy method — it's more reliable with third-party keyboards
  // because it uses the OS text selection flow instead of the Clipboard API permission model
  if (isAndroid()) {
    const legacySuccess = legacyCopy(text);
    if (legacySuccess) {
      return true;
    }
    // Fall through to Clipboard API as last resort
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }

  // Non-Android: try modern Clipboard API first
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to legacy method
    }
  }

  // Fallback for older browsers and Safari edge cases
  try {
    return legacyCopy(text);
  } catch {
    return false;
  }
}

export default copyToClipboard;
